/**
 * IMPORTS
 */

import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// typings
import { SchemaSignInOfUser } from "@pages/signin/schema/signin";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import Logo from "@/common/assets/png/logo-let-scom.png";
// common / const / key-storage
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "@/common/const/key-storage";
import { handleAuthUser } from "@/domain/use-cases/auth/auth";
// utils / notify-messages
import { notifyError, notifySuccess } from "@/utils/notify-messages";

export type SchemaSigninType = z.infer<typeof SchemaSignInOfUser>;

const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);

  const { setIsAuthenticated, isLoading, setIsLoading, setUser, setRole, setFeatures, setToken } =
    useStoreZustandUserAuth();

  // Configuração do React Hook Form com Zod
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SchemaSigninType>({
    resolver: zodResolver(SchemaSignInOfUser),
  });

  const onSubmit = async (data: SchemaSigninType) => {
    const { email, senha } = data;
    setIsLoading(true);

    try {
      const response = await handleAuthUser(email, senha);
      console.log("response", response);

      if (response?.code === 200) {
        const { data, message, access_token, refresh_token } = response;

        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

        setRole(data?.roles);
        setFeatures(data?.features ?? []);
        setToken(access_token);
        setIsAuthenticated(true);
        setUser(data);
        return notifySuccess(message);
      }

      return; // token inválido
    } catch (error: any) {
      return notifyError(error?.message);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="flex h-screen flex-col lg:flex-row">
      {/* Área de Login */}
      <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100 p-8 lg:w-1/2">
        <img src={Logo} alt="logo" className="mb-8 h-[150px] w-[150px]" />
        <div className="w-full lg:w-1/2">
          <h1 className="mb-2 text-2xl font-bold text-neutral900">Seja bem vindo!</h1>
          <p className="mb-6 font-sans text-neutral900">Faça o login para acessar sua conta.</p>
        </div>

        <form className="w-full lg:w-1/2" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-2 block text-lg font-normal text-neutral900">E-mail:</label>
            <input
              {...register("email")}
              id="email"
              type="email"
              name="email"
              className="focus:shadow-outline mb-3 w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
              placeholder="Informe seu e-mail"
            />
            {errors.email && <p className="text-xs italic text-red-500">{errors.email.message}</p>}
          </div>

          <div>
            <label className="mb-2 block text-lg font-normal text-neutral900">Senha:</label>
            <div className="relative">
              <input
                {...register("senha")}
                type={showPassword ? "text" : "password"}
                id="senha"
                name="senha"
                className="focus:shadow-outline mb-3 w-full appearance-none rounded border px-3 py-2 pr-10 leading-tight text-gray-700 shadow focus:outline-none"
                placeholder="Informe sua senha"
              />

              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-2 text-gray-600 hover:text-gray-800"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.senha && <p className="text-xs italic text-red-500">{errors.senha.message}</p>}
          </div>

          <div className="mt-4 flex w-full flex-col items-center justify-center">
            <button
              className="focus:shadow-outline mb-2 w-full rounded bg-neutral900 px-4 py-2 font-bold text-white focus:outline-none"
              type="submit"
            >
              {isLoading ? "Carregando..." : "Login"}
            </button>

            <a
              className="inline-block align-baseline text-sm font-medium text-blue-500 hover:text-blue-800"
              href="recover-password"
            >
              Esqueceu a senha?
            </a>
          </div>
        </form>

        <p className="mt-8 text-xs text-gray-500">Let´scom © 2026 - Todos os direitos reservados</p>
      </div>

      {/* Área de Imagem */}
      <div className="hidden h-full lg:block lg:w-1/2">
        <img
          src="https://media.istockphoto.com/id/1496375910/pt/foto/check-in-using-smartphone-qr-code.jpg?s=2048x2048&w=is&k=20&c=PR20UxjxIamferUw1ZgGfw1hao1APP9WQNLPaOQfEC4="
          alt="Two people shaking hands in a professional setting"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  );
};

/**
 * EXPORTS
 */
export { SignIn };
